/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useEffect, useState } from "react";
// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { styled } from "@mui/system";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";

// Settings page components
import DataTable from "Tables/DataTable";
// import MarketsFilters from "./components/Filters";
import ActionCell from "./components/ActionCell";

// import BasicInfo from "./components/BasicInfo";
// import ChangePassword from "./components/ChangePassword";
// import DeleteAccount from "./components/DeleteAccount";

// react-router components
import { useLocation, useNavigate } from "react-router-dom";

import { getData, postData, putData } from "Utils/server";
import { getAuthorizationHeader, getUserData } from "Utils/helpers";
import { useSoftUIController } from "context";

// sweetalert2 components
import Swal from "sweetalert2";

import DialogBox from "Modals/CheckLeavePage";
import { useCallbackPrompt } from "hooks/useCallbackPrompt";
import SoftSelect from "components/SoftSelect";

// Wizard application components
import FormField from "layouts/applications/wizard/components/FormField";
import SoftButton from "components/SoftButton";

/**
 *
 */
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    //backgroundColor: "#f5f5f9",
    backgroundColor: "#000",
    color: "rgba(f, f, f, 1)", //"rgba(0, 0, 0, 0.87)",
    placement: "left",
    maxWidth: 500,
    minWidth: "minContent",
    height: "minContent",
    textAlign: "left",
    fontSize: "0.65erm",
    border: "1px solid #dadde9",
    boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
    padding: "15px",
    marginLeft: "15px",
  },
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="outlined" {...props} />;
});

let currentData = [];
let sportsList = [];
let marketGroupsList = [];
let marketsFilters = [];
let marketsSetting = {};

let sportsSelectList = [];
let sportsSelected = [];
let marketTypesSelected = [];
let marketGroupsPerSport = {};

let oddSettingsOptions = [];
let defaultStaticOddReactionJson = { value: 0, operator: "+" };

let oddSettingsEditCached = false;
let oddSettingsShowCached = false;

function MarketsSettings({ id, initData, signalCloseModal, checkChanges }) {
  const userData = getUserData();
  const [controller, dispatch] = useSoftUIController();
  const { sports, marketGroups, platform, providers, permissions } = controller;

  const [filtersPersist, setFiltersPersist] = useState({
    filters: {
      markets_type_filter: null,
      markets_status_filter: null,
      sport_filter: { value: 0, label: "All" },
    },
  });
  const [updateEnabled, setUpdateEnabled] = useState(false);
  const [filterToggle, setFilterToggle] = useState(true);
  const [applyFilters, setApplyFilters] = useState(false);
  const [displayData, setDisplayData] = useState(true);
  const [notification, setNotification] = useState(false);
  const [apiResponse, setApiresponse] = useState("success");
  const [apiResponseMsg, setApiResponseMsg] = useState("success");
  const [selected_template, setSelectedTemplate] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [oddSettingsAvailable, setSettingsAvailable] = useState(null);

  const [userPermissions, setUserPermissions] = useState(null);
  const [oddSettingsEdit, setOddSettingsEdit] = useState(false);
  const [oddSettingsShow, setOddSettingsShow] = useState(false);
  const [marketGroupsSelectList, setMarketGroupsSelectList] = useState([]);
  const [marketGroupSelected, setMarketGroupSelected] = useState([]);
  const [marketTypesSelectList, setTypesSelectList] = useState([
    { label: "inPlay", value: "inPlay" },
    { label: "preGame", value: "preGame" },
  ]);
  const [marketTypeSelected, setMarketTypeSelected] = useState([]);

  const [showDialog, setShowDialog] = useState(false);
  const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(showDialog);

  const [hiddenColumns, setHiddenColumns] = useState(["sport_id", "selected"]);
  const [loading, setLoading] = useState(true);

  const [filtered, setFiltered] = useState(false);

  const [marketsFilterNameError, setMarketsFilterNameError] = useState(
    initData && initData.name ? "" : null
  );
  const [marketsFilterName, setMarketsFilterName] = useState(initData ? initData.name : null);

  const [validationErrors, setValidationErrors] = useState("error");

  const [dataTableData, setDataTableData] = useState({
    columns: [
      { Header: "Market id", accessor: "id", width: "100px" },
      { Header: "Sport", accessor: "sport_name", width: "100px" },
      { Header: "selected", accessor: "selected", width: "100px" },
      { Header: "uniName", accessor: "uni_name", width: "100px" },
      { Header: "Groups", accessor: "groups", width: "100px", Cell: formatGroups },
      { Header: "Sport", accessor: "sport_id", width: "100px", Cell: formatSport },
      { Header: "Type", accessor: "type", width: "100px", Cell: formatType },
      {
        Header: "Odds Setting",
        accessor: "odds_filter_id",
        width: "100px",
        Cell: formatOddSetting,
      },
      { Header: "action", accessor: "action", Cell: marketActions, width: "100px" },
    ],

    rows: [],
  });

  const mixingOptions = [
    { value: 0, label: "No Mixing" },
    { value: 1, label: "Highest Price" },
    { value: 2, label: "Percentage Weight" },
  ];

  const oddsReactions = [
    { value: 0, label: "Do Nothing" },
    { value: 1, label: "Suspend Odds" },
    { value: 2, label: "Set Odds to Limits" },
    { value: 3, label: "Suspend Market" },
  ];

  const marginOptions = [
    { value: 0, label: "No Margin" },
    { value: 1, label: "All odds get same margin (EM)" },
    { value: 2, label: "Highest odds get more margin (MPTO)" },
    { value: 3, label: "Lowest odds get more margin (MPTP)" },
  ];

  const oddsSuspensionType = [
    { value: 0, label: "Never" },
    { value: 1, label: "One Provider Suspend Odd" },
    { value: 2, label: "All Providers Suspend Odd" },
    { value: 3, label: "The Most off the Providers Suspend Odd" },
    { value: 4, label: "Ordered Providers Suspend Odd" },
    { value: 5, label: "Price is higher than the sorted Providers" },
  ];

  let navigate = useNavigate();
  let location = useLocation();

  
  const platforms = userData
    ? userData.platforms
      ? userData.platforms.map((platform) => ({
          value: platform.id,
          label: platform.name,
          logo: platform.logo,
          sorting: platform.sorting,
          odds_styles: platform.odds_styles,
        }))
      : []
    : [];

  const getMarketsFilters = () => {
    return marketsFilters;
  };

  async function fetchMarkets() {
    setLoading(true);

    const selectedPlatformId = userData.defaultPlatform.value;
    //`/api/v1/marketsfilters/platform/${selectedPlatformId}/sport/0`
    let getOddsEndpoint = id
      ? `/api/v1/marketsfilters/platform/${selectedPlatformId}/config/${id}/markets`
      : `/api/v1/marketsfilters/platform/${selectedPlatformId}/markets`;

    let response = await getData(getOddsEndpoint, {
      Authorization: getAuthorizationHeader(),
    });
    if (response != null) {
      //console.log(response.data);
      let dataTable = { ...dataTableData };
      setDataTableData({ columns: [], rows: [] });
      setHiddenColumns([]);
      await sleep(1000);

      dataTable.rows = response.data.map((item) => {
        item.oddsLimitReaction = oddsReactions.find((or) => or.value == item.barrier_reaction_type);
        item.oddsLimitReaction = item.oddsLimitReaction ? item.oddsLimitReaction.label : "";
        item.lowerlimit = item.min_odd_barrier;
        item.upperlimit = item.max_odd_barrier;
        item.mixingType = mixingOptions.find((mo) => mo.value == item.mixing_odd_type);
        item.mixingType = item.mixingType || item.mixingType == 0 ? item.mixingType.label : "";
        item.mixing_providers =
          typeof item.mixing_providers == "string"
            ? item.mixing_providers.replace(/[{,},"]/g, "")
            : item.mixing_providers;
        if (item.mixing_providers == "NULL" || !item.mixing_providers) {
          item.mixing_providers = [];
        }
        item.mixingProviders =
          typeof item.mixing_providers == "object" ? item.mixing_providers.join(",") : "";
        item.factor = item.static_odd_reaction ? item.static_odd_reaction.toString() : "0";
        item.randomFactor = item.random_odd_reaction ? "Random Apply" : "Allways Apply";
        item.marginType = marginOptions.find((mo) => mo.value == item.margin_type);
        item.marginType = item.marginType ? item.marginType.label : "";
        item.marginPercent = item.margin_percent;
        item.suspension_type = oddsSuspensionType.find((ost) => ost.value == item.suspension_type);

        let listItems = [
          {
            title: "Market Suspension",
            settings: [
              {
                key: "Action",
                value: item.suspension_type ? item.suspension_type.label : "No Action",
              },
              {
                key: "Providers Priority for Suspension",
                value: item.suspension_provider
                  ? item.suspension_provider
                      .split(",")
                      .map((sp) => providers.find((p) => p.value == sp)["label"])
                  : "-",
              },
            ],
          },
          {
            title: "Odds Limit Reaction",
            settings: [
              { key: "Action", value: item.oddsLimitReaction },
              { key: "min Price", value: item.lowerlimit },
              { key: "max Price", value: item.upperlimit },
            ],
          },
          {
            title: "Mixing Type",
            settings: [
              { key: "Action", value: item.mixingType },
              {
                key: item.mixingType == "No Mixing" ? null : "Mixing",
                value: item.mixingProviders ? item.mixingProviders : "",
              },
              {
                key: "Mixing Odd Limit",
                value: item.mixing_odd_limit ? item.mixing_odd_limit : "-",
              },
            ],
          },
          {
            title: "Price Factor Behaviour",
            settings: [
              {
                key: "Action",
                value:
                  item.static_odd_reaction_json &&
                  item.static_odd_reaction_json.length == 1 &&
                  JSON.stringify(item.static_odd_reaction_json[0].value) ==
                    JSON.stringify(defaultStaticOddReactionJson.value)
                    ? "Do Nothing"
                    : "Prices will be manipulated",
              },
            ],
          },
          {
            title: "Margin Behaviour",
            settings: [
              {
                key: "Action",
                value: item.marginType,
              },
              {
                key: "Margin",
                value: item.marginPercent ? item.marginPercent + "%" : null,
              },
            ],
          },
        ];

        item.odd_settings_info = listItems;

        let groupNamesArray = [];
        item.selected = false;
        item.action = item.filtered;
        item.market_groups.sort();
        for (const key in item.market_groups) {
          if (Object.hasOwnProperty.call(item.market_groups, key)) {
            const groupId = item.market_groups[key];
            const group = marketGroupsList.find((item) => item.id == groupId);
            groupNamesArray.push(group);
            item.sport_name = formatSport({ value: item.sport_id });
          }
          groupNamesArray.sort((a, b) => a.sort - b.sort);

          item.groups = groupNamesArray
            .map((item) => {
              if (!item || !item.name) return "";
              return item.name;
            })
            .join(", ");
        }
        return item;
      });
      currentData = { ...dataTable };
      setHiddenColumns(["sport_id", "selected"]);
      setDataTableData(dataTable);
      //   if (dataTable.rows.length > 0 && !reset) setFilterToggle(false);
      //   if (dataTable.rows.length == 0) setFilterToggle(true);
      //   if (!reset) setDisplayData(true);
    } else {
    }
    setLoading(false);
  }

  async function fetchOddsSettings() {
    let selectedPlatformId = platform.value;
    let getTemplatesEndpoint = `/api/v1/oddsfilters/platforms/${selectedPlatformId}`;
    let response = await getData(getTemplatesEndpoint, { Authorization: getAuthorizationHeader() });
    if (response != null) {
      oddSettingsOptions = response.data.map((r) => {
        return { value: r.id, label: r.name };
      });
      setSettingsAvailable(oddSettingsOptions);
    }
  }

  function formatSport({ value, ...rest }) {
    let sport = sportsList ? sportsList.find((item) => item.id == value) : null;
    return sport ? sport["name"] : "";
  }

  function formatGroups({ value, ...rest }) {
    
    return (
      <>
        {value.split(", ").map((item, index) => {
          return (
            <SoftBadge
              style={{ marginRight: "3px" }}
              key={item}
              variant="gradient"
              color={item == "Main" ? "dark" : "light"}
              size="xs"
              badgeContent={item}
              container
            />
          );
        })}
      </>
    );
  }

  function formatType({ value, ...rest }) {
    
    return (
      <SoftBadge
        style={{ marginRight: "3px" }}
        key={value}
        variant="gradient"
        color={value == "inPlay" ? "info" : "secondary"}
        size="xs"
        badgeContent={value}
        container
      />
    );
  }

  function formatOddSetting({ value, ...rest }) {
    return value ? (
      <SoftBox className="odd-setting-menu-holder">
        {/* <SoftSelect
          customSmall={true}
          options={oddSettingsAvailable}
          value={oddSettingsAvailable.find((item) => item.value === value)}
        ></SoftSelect> */}
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{ cursor: "pointer", lineHeight: 0, fontSize: "1rem", verticalAlign: "sub" }}
        >
          <Tooltip title="Change Odd Settings" placement="top">
            <Icon
              style={{
                display: rest.row.original.odds_setting ? "" : "none",
                color: rest.row.original.odds_setting
                  ? rest.row.original.operOddSettings
                    ? "#ff000070"
                    : "rgb(203, 12, 159)"
                  : "",
              }}
              onClick={(e) =>
                handleOddSettingSelection(e, { action: "add", data: rest.row.original.id })
              }
            >
              {rest.row.original.odds_setting
                ? rest.row.original.operOddSettings
                  ? "playlist_remove_icon"
                  : "playlist_add_circle_icon"
                : "playlist_add_icon"}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          style={{
            display:
              rest.row.original.odds_setting && !rest.row.original.operOddSettings ? "" : "none",
          }}
          sx={{
            marginLeft: "5px",
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
          }}
        >
          <Tooltip title="Reset Changes" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, { action: "remove", data: rest.row.original.id })
              }
            >
              undo_icon{" "}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftSelect
          className={rest.row.original.operOddSettings ? "odd-setting-menu" : "hide"}
          size="small"
          customSmall={true}
          options={oddSettingsOptions}
          value={
            oddSettingsOptions
              ? oddSettingsOptions.find((item) => item.value === rest.row.original.odds_setting)
              : null
          }
          onChange={(item) => rowOddSettingsChange(item, rest.row.original)}
        ></SoftSelect>
        <HtmlTooltip
          placement="right"
          title={
            <React.Fragment>
              <div style={{}}>
                {rest.row.original.odd_settings_info.map((item, index) => {
                  return (
                    <div key={index} style={{}}>
                      <div style={{ fontWeight: "bold" }}>{item.title}</div>
                      <ul style={{ marginLeft: "5px", paddingLeft: "10px" }}>
                        {item.settings.map((s, i) => {
                          let print = s.value ? (s.key ? s.key + ": " + s.value : s.value) : "";
                          return print == "" ? false : <li key={i}>{print}</li>;
                        })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </React.Fragment>
          }
        >
          <Button
            sx={{
              maxWidth: "15px",
              width: "15px",
              minWidth: "10px",
              marginRight: "5px",
              display: rest.row.original.odds_setting ? "none" : "",
            }}
          >
            <Icon>info_outlined</Icon>
          </Button>
        </HtmlTooltip>

        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            marginRight: "5px",
            display: oddSettingsEditCached
              ? rest.row.original.odds_setting
                ? "none"
                : ""
              : "none",
          }}
        >
          <Tooltip title="Edit Filter's Odd Settings" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, { action: "edit", data: rest.row.original.id })
              }
            >
              edit
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display: oddSettingsEditCached
              ? rest.row.original.odds_setting
                ? "none"
                : ""
              : "none",
          }}
        >
          <Tooltip title="Delete Filter's Odd Settings" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, { action: "delete", data: rest.row.original.id })
              }
            >
              delete_outlined
            </Icon>
          </Tooltip>
        </SoftTypography>
      </SoftBox>
    ) : (
      <SoftBox className="odd-setting-menu-holder">
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display: oddSettingsEditCached ? "" : "none",
          }}
        >
          <Tooltip
            title={
              rest.row.original.odds_setting ? "Change Odd Settings" : "Add Filter's Odd Settings"
            }
            placement="top"
          >
            <Icon
              style={{
                color: rest.row.original.odds_setting
                  ? "rgb(203, 12, 159)"
                  : rest.row.original.operOddSettings
                  ? "#ff000070"
                  : "",
              }}
              onClick={(e) =>
                handleOddSettingSelection(e, { action: "add", data: rest.row.original.id })
              }
            >
              {rest.row.original.odds_setting
                ? "playlist_add_circle_icon"
                : rest.row.original.operOddSettings
                ? "playlist_remove_icon"
                : "playlist_add_icon"}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          sx={{
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
            display:
              oddSettingsShowCached && !oddSettingsEditCached && !rest.row.original.odds_setting
                ? ""
                : "none",
          }}
        >
          None
        </SoftTypography>
        <SoftTypography
          variant="text"
          color="secondary"
          style={{
            display:
              rest.row.original.odds_setting && !rest.row.original.operOddSettings ? "" : "none",
          }}
          sx={{
            marginLeft: "5px",
            cursor: "pointer",
            lineHeight: 0,
            fontSize: "1rem",
            verticalAlign: "sub",
          }}
        >
          <Tooltip title="Reset Changes" placement="top">
            <Icon
              onClick={(e) =>
                handleOddSettingSelection(e, { action: "remove", data: rest.row.original.id })
              }
            >
              undo_icon{" "}
            </Icon>
          </Tooltip>
        </SoftTypography>
        <SoftSelect
          className={rest.row.original.operOddSettings ? "odd-setting-menu" : "hide"}
          size="small"
          customSmall={true}
          options={oddSettingsOptions}
          value={
            oddSettingsOptions
              ? oddSettingsOptions.find(
                  (item) => item.value === value || item.value === rest.row.original.odds_setting
                )
              : null
          }
          onChange={(item) => rowOddSettingsChange(item, rest.row.original)}
        ></SoftSelect>
      </SoftBox>
    );
  }

  function marketActions({ value, ...rest }) {
    return (
      <ActionCell
        id={rest.row.original.id}
        filtered={rest.row.original.filtered}
        row={rest.row}
        handleApplyAction={handleSignals}
      />
    );
  }

  async function handleSignals(e, id, row, filtered) {
    e.preventDefault();
    //console.log(row);
    setUpdateEnabled(false);
    setHiddenColumns([]);
    setDataTableData({ columns: [], rows: [] });

    let dataRow = currentData.rows.find((item) => item.id == row.original.id);
    if (e.type != "OddSettingsChange") {
      if (dataRow.hasOwnProperty("operFiltered")) {
        dataRow.operFiltered = !dataRow.operFiltered;
        if (!filtered && !dataRow.operFiltered) {
          delete dataRow.operFiltered;
        }
        if (filtered && dataRow.operFiltered) {
          delete dataRow.operFiltered;
        }
      } else {
        dataRow.operFiltered = filtered ? false : true;
        if (dataRow.operFiltered) dataRow.modified = true;
        else dataRow.modified = false;
      }
    }

    const marketFilter = {
      market_id: id,
      provider_id: 1000,
      platform_id: userData.defaultPlatform.value,
      template_id: row.original.template_id,
      filtered: dataRow.filtered,
      operFiltered: dataRow.operFiltered,
      odds_setting: dataRow.odds_setting ? dataRow.odds_setting : dataRow.odds_filter_id,
      sport_id: dataRow.sport_id,
      odds_filter_config_id: dataRow.odds_filter_config_id,
      type: dataRow.type,
    };
    const marketFilterIndex = marketsFilters.findIndex(
      (item) => item.market_id === marketFilter.market_id
    );

    if (!dataRow.hasOwnProperty("operFiltered") && !dataRow.hasOwnProperty("odds_setting")) {
      if (marketFilterIndex > -1) {
        marketsFilters.splice(marketFilterIndex, 1);
        dataRow.modified = false;
      }
      if (marketsFilters.length > 0) setUpdateEnabled(true);

      if (marketFilterIndex > -1 && marketsFilters.length == 0) {
        setApiresponse("error");
        setApiResponseMsg("There are NO unsaved market filters changes.");
        setNotification(true);
        setShowDialog(false);
      }

      // setHiddenColumns(["sport_id", "selected"]);
      // setDataTableData(currentData);
      handleSelectionValueChange(null, null);
      return;
    } else {
      dataRow.modified = true;
    }

    // setHiddenColumns(["sport_id", "selected"]);
    // setDataTableData(currentData);
    handleSelectionValueChange(null, null);

    if (marketFilterIndex < 0) {
      marketsFilters.push(marketFilter);
    } else {
      marketsFilters[marketFilterIndex] = marketFilter;
    }

    if (marketsFilters.length > 0) {
      setUpdateEnabled(true);
      setApiresponse("success");
      setApiResponseMsg("There are unsaved market filters changes.");
      setNotification(true);
      setShowDialog(true);
    }
  }

  
  const sleep = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const handleUpdate = () => {
    Swal.fire({
      title: "We proccess your request! ",
      html: "Wait for few seconds...",
      timerProgressBar: true,
      didOpen: async () => {
        Swal.showLoading();

        const marketsFiltersEndPoint = `/api/v1/marketsfilters/platform/${platform.value}/config`;
        const marketsFiltersVar = [];
        marketsSetting.filter_id = id;
        marketsFiltersVar.push(marketsSetting);

        const response = await putData(marketsFiltersEndPoint, marketsFiltersVar, {
          Authorization: getAuthorizationHeader(),
        });

        if (!response) {
          Swal.clickDeny();
        } else {
          if (response.status === 200) {
            const marketsFiltersEndPoint = `/api/v1/marketsfilters/platform/${platform.value}`;
            const marketsFiltersVar = getMarketsFilters();
            if (marketsFiltersVar.length > 0) {
              marketsFiltersVar.map((m) => (m.markets_filters_config_id = id));

              const response = await putData(marketsFiltersEndPoint, marketsFiltersVar, {
                Authorization: getAuthorizationHeader(),
              });
            }

            Swal.clickConfirm();
          }
        }
      },
      willClose: () => {},
      allowOutsideClick: false,
    }).then((result) => {
      /* Read more about handling dismissals below */
      if (result.dismiss === Swal.DismissReason.timer) {
        //console.log("I was closed by the timer");
        //routeChange();
      }

      setUpdateEnabled(true);
      setUpdateEnabled(false);
      resetData();
      setShowDialog(false);
      if (result.isConfirmed) {
        Swal.fire("Request Sent!", `Live Data sent to the platform`, "success");
        signalCloseModal(new Event("cancel"), true);
      }
      if (result.isDenied) {
        Swal.fire(
          "Error!",
          `Live Data didn't send to the platform. Please try again later!`,
          "error"
        );
      }
    });
  };

  const handleCreate = () => {
    Swal.fire({
      title: "We proccess your request! ",
      html: "Wait for few seconds...",
      timerProgressBar: true,
      didOpen: async () => {
        Swal.showLoading();
        const marketsFiltersEndPoint = `/api/v1/marketsfilters/platform/${platform.value}/config`;
        const marketsFiltersVar = marketsSetting;

        const response = await postData(marketsFiltersEndPoint, marketsFiltersVar, {
          Authorization: getAuthorizationHeader(),
        });

        if (!response) {
          Swal.clickDeny();
        } else {
          if (response.status === 200) {
            const setting_id = response.data.id;
            const marketsFiltersEndPoint = `/api/v1/marketsfilters/platform/${platform.value}`;
            const marketsFiltersVar = getMarketsFilters();

            marketsFiltersVar.map((m) => (m.markets_filters_config_id = setting_id));

            const response2 = await putData(marketsFiltersEndPoint, marketsFiltersVar, {
              Authorization: getAuthorizationHeader(),
            });

            Swal.clickConfirm();
          }
        }
      },
      willClose: () => {},
      allowOutsideClick: false,
    }).then((result) => {
      /* Read more about handling dismissals below */
      if (result.dismiss === Swal.DismissReason.timer) {
        //console.log("I was closed by the timer");
        //routeChange();
      }

      setUpdateEnabled(true);
      setUpdateEnabled(false);
      setShowDialog(false);
      if (result.isConfirmed) {
        Swal.fire("Request Sent!", `Live Data sent to the platform`, "success");
        signalCloseModal(new Event("cancel"), true);
      }
      if (result.isDenied) {
        Swal.fire(
          "Error!",
          `Live Data didn't send to the platform. Please try again later!`,
          "error"
        );
      }
    });
  };

  const handleCancelEdit = async () => {
    if (marketsFilters.length > 0) {
      Swal.fire({
        title: `Unsaved Data!`,
        html:
          "You are going Lose some filtered Markets you have already set.</br></br>" +
          marketsFilters.length +
          " Market filters have been changed. </br></br> Are you Sure?",
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonText: "Yes, Cancel them!",
        denyButtonText: "Cancel",
        icon: "warning",
        confirmButtonColor: "#87cb16",
        denyButtonColor: "#ff4a55",
      }).then(async (result) => {
        if (result.isConfirmed) {
          //console.log("Modal confirmed");
          setUpdateEnabled(true);
          setUpdateEnabled(false);
          resetData();
          setShowDialog(false);
        }
      });
    } else {
      //console.log("Nothing have been changed");
    }
  };

  const filterModified = () => {
    let tmpFiltered = filtered;
    setFiltered(!tmpFiltered);

    setHiddenColumns([]);
    setDataTableData({ columns: [], rows: [] });
    if (tmpFiltered) {
      setHiddenColumns(["sport_id", "selected"]);
      setDataTableData({ columns: currentData.columns, rows: currentData.rows });
    } else {
      let dataRows = currentData.rows.filter((item) => item.modified == true);
      setHiddenColumns(["sport_id", "selected"]);
      setDataTableData({ columns: currentData.columns, rows: dataRows });
    }
  };

  async function resetData() {
    setHiddenColumns([]);
    setDataTableData({ columns: [], rows: [] });
    setUpdateEnabled(false);
    marketsFilters = [];
    let dataRows = currentData.rows;
    if (!dataRows) return;
    for (let index = 0; index < dataRows.length; index++) {
      const dataRow = dataRows[index];
      dataRow.selected = false;
      dataRow.modified = false;
      dataRow.operFiltered = null;
      delete dataRow.odds_setting;
      delete dataRow.operOddSettings;
      delete dataRow.operFiltered;
    }
    setHiddenColumns(["sport_id", "selected"]);

    setSelectedRows([]);
    // setDataTableData(currentData);
    handleSelectionValueChange(null, null);
    setShowDialog(false);
    setFiltered(false);
  }

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setNotification(false);
  };

  const handleTableDataChanges = async ({ key, value, extra }) => {
    //console.log(key, ":", value, extra);
    if (key == "refresh") {
      if (filtersPersist) fetchMarkets(filtersPersist.filters, filtersPersist.reset);
    }

    if (key == "selectOne") {
      setUpdateEnabled(false);
      setHiddenColumns([]);
      setDataTableData({ columns: [], rows: [] });

      let dataRow = currentData.rows.find((item) => item.id == value);
      dataRow.selected = extra;
      let rows = selectedRows;
      let rowExist = rows.findIndex((item) => item.id == value);
      if (rowExist > -1) {
        if (!extra) {
          rows.splice(rowExist, 1);
          setSelectedRows(rows);
        }
      } else {
        if (extra) {
          rows.push(dataRow);
          setSelectedRows(rows);
        }
      }
      if (marketsFilters.length > 0) setUpdateEnabled(true);

      setHiddenColumns(["sport_id", "selected"]);
      //setDataTableData(currentData);
      await handleSelectionValueChange(null, null);
    }

    if (key == "selectAll") {
      setUpdateEnabled(false);
      setHiddenColumns([]);
      setDataTableData({ columns: [], rows: [] });
      let dataRows = currentData.rows;
      if (extra == "all") {
        dataRows = value;
        for (let index = 0; index < dataRows.length; index++) {
          const dataRow = dataRows[index];
          dataRow.selected = true;
        }
        setSelectedRows(dataRows);
      } else if (extra == "none") {
        dataRows = value;
        for (let index = 0; index < dataRows.length; index++) {
          const dataRow = dataRows[index];
          dataRow.selected = false;
        }
        setSelectedRows([]);
      }
      if (marketsFilters.length > 0) setUpdateEnabled(true);

      setHiddenColumns(["sport_id", "selected"]);
      // setDataTableData(currentData);
      await handleSelectionValueChange(null, null);
    }
  };

  function handleOddSettingSelection(e, { action, data }) {
    e.preventDefault();

    setUpdateEnabled(false);
    setHiddenColumns([]);
    setDataTableData({ columns: [], rows: [] });
    let row = currentData.rows.find((row) => row.id == data);
    let sendSignal = false;

    if (action == "edit") {
      if (row) {
        row.operOddSettings = row.operOddSettings ? false : true;
        if (row.operOddSettings) {
          row.odds_setting = row.odds_filter_id;
        } else {
          if (row.odds_filter_id) {
            delete row.odds_setting;
            sendSignal = true;
          }
        }
      }
    }

    if (action == "add") {
      if (row) {
        if (row.odds_setting == row.odds_filter_id && row.odds_setting != null) {
          delete row.odds_setting;
          sendSignal = true;
        }
        row.operOddSettings = row.operOddSettings ? false : true;
      }
    }

    if (action == "remove") {
      if (row) {
        delete row.odds_setting;
        delete row.operOddSettings;
        sendSignal = true;
      }
    }

    if (action == "delete") {
      if (row) {
        row.odds_filter_id = null;
        row.odds_setting = null;
        row.operOddSettings = false;
        sendSignal = true;
      }
    }

    if (sendSignal)
      handleSignals(new Event("OddSettingsChange"), row.id, { original: row }, row.filtered);

    if (marketsFilters.length > 0) setUpdateEnabled(true);
    // setHiddenColumns(["sport_id", "selected"]);
    // setDataTableData(currentData);
    handleSelectionValueChange(null, null);
  }

  function oddSettingsChange(item) {
    for (let index = 0; index < selectedRows.length; index++) {
      const market = selectedRows[index];
      market.odds_setting = item.value;
      market.modified = true;
      handleSignals(
        new Event("OddSettingsChange"),
        market.id,
        { original: market },
        market.filtered
      );
    }
  }

  function statusChange(item) {
    for (let index = 0; index < selectedRows.length; index++) {
      const market = selectedRows[index];
      market.operFiltered = item.value == 0 ? false : true;
      market.modified = true;
      handleSignals(new Event("StatusChange"), market.id, { original: market }, market.filtered);
    }
  }

  function rowOddSettingsChange(item, row) {
    row.odds_setting = item.value;
    row.modified = true;
    row.operOddSettings = false;
    handleSignals(new Event("OddSettingsChange"), row.id, { original: row }, row.filtered);
  }

  const handlePermissions = (permissions) => {
    setUserPermissions(permissions);
    let editOddSettingsEnabled = false;
    let showOddSettingsEnabled = false;

    //console.log(permissions);

    for (let index = 0; index < permissions.length; index++) {
      const permission = permissions[index];
      if (permission == "super") {
        editOddSettingsEnabled = true;
        showOddSettingsEnabled = true;
      }
      if (permission == "feed-settings-odds-edit") {
        editOddSettingsEnabled = true;
        showOddSettingsEnabled = true;
        break;
      }
      if (permission == "feed-settings-odds-show") {
        showOddSettingsEnabled = true;
      }
    }

    setOddSettingsEdit(editOddSettingsEnabled);
    oddSettingsEditCached = editOddSettingsEnabled;
    setOddSettingsShow(showOddSettingsEnabled);
    oddSettingsShowCached = showOddSettingsEnabled;
  };

  const handleSignalCloseModal = (e) => {
    if (!e) return;
    e.preventDefault();

    if (!updateEnabled) {
      signalCloseModal(new Event("cancel"));
      return;
    }

    if (updateEnabled) {
      Swal.fire({
        title: `Unsaved Data!`,
        html: `You have unsaved changes! </br>Are you sure you want to cancel them?`,
        showDenyButton: true,
        showConfirmButton: true,
        confirmButtonText: "Yes",
        denyButtonText: "Cancel",
        icon: "warning",
        confirmButtonColor: "#87cb16",
        denyButtonColor: "#ff4a55",
      }).then(async (result) => {
        if (result.isConfirmed) {
          signalCloseModal(new Event("cancel"));
        } else {
          signalCloseModal(new Event("cancel cancel"));
        }
      });
    } else {
      signalCloseModal(new Event("cancel"));
    }
  };

  const handleInputValueChange = (e) => {
    e.preventDefault();
    setMarketsFilterName(e.currentTarget.value);
    marketsSetting.name = e.currentTarget.value;
    marketsSetting.platform_id = platform.value;
    validateData();
  };

  const handleFocusOut = (e) => {
    e.preventDefault();
    setMarketsFilterName(e.currentTarget.value);
    marketsSetting.name = e.currentTarget.value;
    marketsSetting.platform_id = platform.value;
    validateData();
  };

  const validateData = () => {
    let marketConfigNameError = marketsFilterNameError;

    if (marketsFilterName && marketsFilterName != "") {
      marketConfigNameError = "";
      setMarketsFilterNameError(marketConfigNameError);
    }

    if (marketConfigNameError != "") {
      setValidationErrors("There are some errors");
    } else {
      if (updateEnabled) {
        setValidationErrors(null);
      } else {
        if (initData && marketsFilterName == initData.name) {
          //console.log("no valid 2", marketConfigNameError, " - ", updateEnabled);
          setValidationErrors("There are some errors");
        } else {
          setValidationErrors(null);
        }
      }
    }
  };

  const handleSelectionValueChange = async (item, type) => {
    // console.log(item);
    if (type == "sport") {
      sportsSelected = item.map((s) => s.value);
      setMarketGroupsSelectList([]);
      if (item.length == 0) setMarketGroupSelected([]);
      const marketGroupsFiltered = marketGroupSelected.filter((mgs) =>
        sportsSelected.includes(mgs.sport_id)
      );
      setMarketGroupSelected(marketGroupsFiltered);
      marketGroupsPerSport = {};
      for (let index = 0; index < item.length; index++) {
        const mg = item[index];
        marketGroupsPerSport[mg.value] = [];
      }
      for (let index = 0; index < marketGroupsFiltered.length; index++) {
        const mgf = marketGroupsFiltered[index];
        marketGroupsPerSport[mgf.sport_id].push(mgf.value);
      }
    }

    if (type == "marketGroup") {      setMarketGroupSelected(item);
      for (let index = 0; index < Object.keys(marketGroupsPerSport).length; index++) {
        const key = Object.keys(marketGroupsPerSport)[index];
        marketGroupsPerSport[key] = [];
      }
      for (let index = 0; index < item.length; index++) {
        const mgf = item[index];
        marketGroupsPerSport[mgf.sport_id].push(mgf.value);
      }
    }

    if (type == "marketTypes") {
      marketTypesSelected = item.map((s) => s.value);
      setMarketTypeSelected(item);
    }

    let dataTable = { ...currentData };

    dataTable.rows = currentData.rows.filter((cd) => {
            if (sportsSelected.length == 0) {
        if (marketTypesSelected.length == 0) return true;
        return marketTypesSelected.includes(cd.type);
      }
      if (marketGroupsPerSport) {
        if (marketGroupsPerSport[cd.sport_id]) {
          if (marketGroupsPerSport[cd.sport_id].length == 0) {
            if (marketTypesSelected.length == 0) return true;
            return marketTypesSelected.includes(cd.type);
          }
          if (
            marketGroupsPerSport[cd.sport_id].filter((mgs) => cd.market_groups.includes(mgs))
              .length > 0
          ) {
            if (marketTypesSelected.length == 0) return true;
            return marketTypesSelected.includes(cd.type);
          }
          return false;
        }
      }

      return false;
    });
    setHiddenColumns(["sport_id", "selected"]);
    setDataTableData(dataTable);
  };

  useEffect(() => {
    if (checkChanges) handleSignalCloseModal(new Event("checkChanges"));
  }, [checkChanges]);

  useEffect(() => {
    if (sports) {
      sportsList = sports;
      sportsSelectList = sports.map((s) => {
        return { value: s.id, label: s.name };
      });
    }
  }, [sports]);

  useEffect(() => {
    if (permissions.length > 0) handlePermissions(permissions);
  }, [permissions]);

  useEffect(() => {
    if (marketGroups) {
      marketGroupsList = marketGroups;
    }
  }, [marketGroups]);

  useEffect(() => {
    setMarketGroupsSelectList(
      marketGroupsList
        .map((mg1) => {
          return {
            value: mg1.id,
            label: `${sportsList.find((s) => s.id == mg1.sport_id)["name"]} - ${mg1.name}`,
            sport_id: mg1.sport_id,
          };
        })
        .filter((mg) => {
          return sportsSelected.indexOf(mg.sport_id) > -1;
        })
        .sort((a, b) => {
          if (a.label < b.label) return -1;
          if (a.label > b.label) return 1;
          return 0;
        })
    );
  }, [sportsSelected]);

  useEffect(() => {
    if (platform) {
      fetchOddsSettings();
      resetData();
      fetchMarkets();
    }
  }, [platform]);

  useEffect(() => {
    validateData();
    if (marketsFilterName) marketsSetting.name = marketsFilterName;
  }, [marketsFilterName, marketsFilterNameError, updateEnabled]);

  return (
    <>
      <DialogBox
        // @ts-ignore
        showDialog={showPrompt}
        confirmNavigation={confirmNavigation}
        cancelNavigation={cancelNavigation}
      />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        key={"topright"}
        open={notification}
        autoHideDuration={6000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={`${apiResponse}`}
          sx={{ width: "100%" }}
          className={`api-${apiResponse}-notifications`}
        >
          {apiResponseMsg}
        </Alert>
      </Snackbar>
      <SoftBox
        my={0}
        sx={{
          marginBottom: "0px",
          overflow: "auto",
          height: "calc(100vh - 200px)",
          scrollbarWidth: "thin",
          paddingBottom: "50px",
        }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} lg={12} sx={{ marginLeft: "0px !important" }}>
            <SoftBox>
              <Grid container spacing={2} item xs={8} md={8}>
                <Grid item xs={4} md={4}>
                  <FormField
                    id="market-filter-name"
                    type="text"
                    error={marketsFilterNameError != ""}
                    success={marketsFilterNameError == ""}
                    name="market-filter-name"
                    label="Markets Settings Name"
                    info="The name will be visible in order to apply in filters "
                    infoPlacement="top"
                    infoWidth="200"
                    placeholder="Eg. My Markets Settings"
                    defaultValue={marketsFilterName}
                    onChange={handleInputValueChange}
                    onBlur={handleFocusOut}
                  />
                  <span className="validation-error">{marketsFilterNameError}</span>
                </Grid>
              </Grid>
            </SoftBox>
            <SoftBox style={{ display: displayData ? "" : "none" }}>
              <Grid container spacing={0}>
                <Card style={{ width: "100%" }}>
                  <Grid item xs={12} sx={{ paddingTop: "0px", height: "100%" }}>
                    <SoftBox
                      style={{
                        background: "#f9f9f9",
                        paddingTop: "10px",
                        paddingLeft: "10px",
                        paddingBottom: "10px",
                      }}
                    >
                      Filters:
                      <Grid container style={{ zIndex: 4, position: "relative" }}>
                        <Grid item style={{ maxWidth: "350px" }}>
                          <SoftTypography variant="caption">Sport: </SoftTypography>
                          <SoftSelect
                            style={{ backgroundColor: "#fff" }}
                            className="multiselectwrap"
                            isMulti={true}
                            size="small"
                            customSmall={true}
                            options={sportsSelectList}
                            onChange={(item) => handleSelectionValueChange(item, "sport")}
                          ></SoftSelect>
                        </Grid>
                        <Grid item style={{ maxWidth: "350px" }}>
                          <SoftTypography variant="caption">Groups: </SoftTypography>
                          <SoftSelect
                            style={{ backgroundColor: "#fff" }}
                            className="multiselectwrap"
                            value={marketGroupSelected}
                            isMulti={true}
                            size="small"
                            customSmall={true}
                            options={marketGroupsSelectList}
                            onChange={(item) => handleSelectionValueChange(item, "marketGroup")}
                          ></SoftSelect>
                        </Grid>
                        <Grid item style={{ maxWidth: "350px" }}>
                          <SoftTypography variant="caption">Types: </SoftTypography>
                          <SoftSelect
                            style={{ backgroundColor: "#fff" }}
                            className="multiselectwrap"
                            value={marketTypeSelected}
                            isMulti={true}
                            size="small"
                            customSmall={true}
                            options={marketTypesSelectList}
                            onChange={(item) => handleSelectionValueChange(item, "marketTypes")}
                          ></SoftSelect>
                        </Grid>
                      </Grid>
                    </SoftBox>
                    <SoftBox
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      pt={2}
                      pl={3}
                      pr={3}
                    >
                      <SoftBox lineHeight={1}>
                        <SoftTypography variant="h5" fontWeight="medium">
                          {""}
                        </SoftTypography>
                        <SoftTypography variant="button" fontWeight="regular" color="text">
                          {""}
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox lineHeight={1}>
                        <Button onClick={filterModified} disabled={updateEnabled ? false : true}>
                          {filtered ? "Show All" : "Show Modified"}
                        </Button>
                        <Button onClick={handleCancelEdit} disabled={updateEnabled ? false : true}>
                          Reset
                        </Button>
                        {/* <Button onClick={handleUpdate} disabled={updateEnabled ? false : true}>
                          Update
                        </Button> */}
                      </SoftBox>
                    </SoftBox>
                    <SoftBox
                      pt={0}
                      pl={3}
                      pr={3}
                      sx={{
                        display: oddSettingsEditCached
                          ? selectedRows.length > 0
                            ? ""
                            : "none"
                          : "none",
                        position: "relative",
                        marginLeft: "auto",
                        marginRight: "auto",
                        right: "0px",
                        height: "40px",
                      }}
                      className="table-multiselected-action-market-settings"
                    >
                      <Grid
                        container
                        sx={{
                          backgroundImage: "linear-gradient(to right, #FFFFFF, #f8f9fa, #ffffff)",
                          minHeight: "90px",
                          maxHeight: "90px",
                          color: "red !important",
                          // textAlign: "right !important",
                        }}
                      >
                        <Grid item mr={2} md={4} sm={4} sx={{ textAlign: "right !important" }}>
                          <SoftTypography variant="caption"> </SoftTypography>
                          <SoftTypography
                            variant="caption"
                            style={{
                              textAlign: "right !important",
                              lineHeight: 10,
                              width: "100px !important",
                              maxWidth: "230px !important",
                            }}
                          >
                            Apply to the selected Markets:{" "}
                          </SoftTypography>
                        </Grid>
                        <Grid item mr={2} style={{ zIndex: 3 }}>
                          <SoftTypography variant="caption">Odds Settings: </SoftTypography>
                          <SoftSelect
                            size="small"
                            customSmall={true}
                            options={oddSettingsOptions}
                            onChange={(item) => oddSettingsChange(item)}
                          ></SoftSelect>
                        </Grid>
                        <Grid item></Grid>
                        <Grid item style={{ zIndex: 3 }}>
                          <SoftTypography variant="caption">Status: </SoftTypography>
                          <SoftSelect
                            size="small"
                            customSmall={true}
                            options={[
                              { value: 0, label: "Disable" },
                              { value: 1, label: "Enable" },
                            ]}
                            onChange={(item) => statusChange(item)}
                          ></SoftSelect>
                        </Grid>
                      </Grid>
                    </SoftBox>
                    <DataTable
                      table={dataTableData}
                      tableName="marketsSettings"
                      bodyClassName="settlement-fixtures-list"
                      entriesPerPage={{
                        defaultValue: 10,
                        entries: [5, 10, 15, 20, 25],
                      }}
                      openBackDrop={loading}
                      hiddenColumns={hiddenColumns}
                      autoResetPageDefault={false}
                      autoResetGlobalFilter={true}
                      autoResetSortBy={true}
                      canSearch
                      checkboxSelection={oddSettingsEditCached}
                      refreshEnabled={false}
                      informParent={handleTableDataChanges}
                    ></DataTable>
                  </Grid>
                </Card>
              </Grid>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
      <SoftBox className="modal-footer-buttons">
        <Grid container justifyContent="right">
          <Grid item>
            <SoftButton variant="outlined" color="info" onClick={handleSignalCloseModal}>
              Cancel
            </SoftButton>
          </Grid>
          <Grid item>
            <SoftButton
              type="submit"
              variant="gradient"
              color="dark"
              onClick={id ? handleUpdate : handleCreate}
              disabled={validationErrors ? true : false}
            >
              Update
            </SoftButton>
          </Grid>
        </Grid>
      </SoftBox>
    </>
  );
}

// Typechecking props for the DataTable
MarketsSettings.propTypes = {
  id: PropTypes.number,
  initData: PropTypes.object,
  signalCloseModal: PropTypes.func,
  checkChanges: PropTypes.bool,
};

export default MarketsSettings;
